import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { formatIDR } from "@/lib/format";
import { PageHeader, Loading, EmptyState, Field, SkeletonTable } from "@/components/ui-helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Search, Pencil, Eye } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", whatsapp: "", email: "", instagram: "", address: "", city: "", province: "", postcode: "", notes: "" };

export default function Customers() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 30;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/customers", { params: { search: search || undefined, page: 1, page_size: PAGE_SIZE } });
      setItems(r.data.items); setTotal(r.data.total); setHasMore(r.data.has_more); setPage(1);
    } finally { setLoading(false); }
  }, [search]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const next = page + 1;
      const r = await api.get("/customers", { params: { search: search || undefined, page: next, page_size: PAGE_SIZE } });
      setItems((prev) => [...prev, ...r.data.items]); setHasMore(r.data.has_more); setPage(next);
    } finally { setLoadingMore(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c }); setOpen(true); };

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1") { openNew(); searchParams.delete("new"); setSearchParams(searchParams, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const save = async () => {
    if (!form.name) return toast.error("Nama wajib diisi");
    try {
      if (editing) { await api.put(`/customers/${editing.id}`, form); toast.success("Pelanggan diperbarui"); }
      else { await api.post("/customers", form); toast.success("Pelanggan ditambahkan"); }
      setOpen(false); load();
    } catch { toast.error("Gagal menyimpan"); }
  };

  return (
    <div>
      <PageHeader title="Pelanggan" subtitle="Database pelanggan Anda">
        <Button onClick={openNew} data-testid="customer-create-button"><Plus className="h-4 w-4 mr-1.5" />Tambah Pelanggan</Button>
      </PageHeader>
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input className="pl-9" placeholder="Cari nama, WhatsApp, Instagram..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="customers-search-input" />
      </div>

      {loading ? <SkeletonTable /> : items.length === 0 ? (
        <EmptyState title="Belum ada pelanggan" action={<Button onClick={openNew}><Plus className="h-4 w-4 mr-1.5" />Tambah</Button>} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => (
            <div key={c.id} className="rounded-xl border border-border bg-card p-4 shadow-sm" data-testid="customer-card">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold">{c.name[0]}</div>
                <div className="flex-1 min-w-0"><p className="font-medium truncate">{c.name}</p><p className="text-xs text-muted-foreground">{c.whatsapp}</p></div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                <div><p className="text-xs text-muted-foreground">Order</p><p className="font-semibold text-sm">{c.total_orders || 0}</p></div>
                <div><p className="text-xs text-muted-foreground">Belanja</p><p className="font-mono text-xs">{formatIDR(c.total_spending)}</p></div>
                <div><p className="text-xs text-muted-foreground">Piutang</p><p className="font-mono text-xs text-destructive">{formatIDR(c.outstanding_balance)}</p></div>
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => navigate(`/customers/${c.id}`)} data-testid="customer-detail-button"><Eye className="h-3.5 w-3.5 mr-1" />Lihat Semua Order</Button>
                <Button size="sm" variant="outline" onClick={() => openEdit(c)}><Pencil className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {!loading && items.length > 0 && (
        <div className="mt-4 flex flex-col items-center gap-2">
          <p className="text-xs text-muted-foreground" data-testid="customers-count">Menampilkan {items.length} dari {total} pelanggan</p>
          {hasMore && <Button variant="outline" onClick={loadMore} disabled={loadingMore} data-testid="customers-load-more">{loadingMore ? "Memuat…" : "Muat lebih banyak"}</Button>}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Pelanggan" : "Tambah Pelanggan"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nama" required><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="customer-name-input" /></Field>
            <Field label="WhatsApp"><Input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="628..." data-testid="customer-wa-input" /></Field>
            <Field label="Email"><Input value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
            <Field label="Instagram"><Input value={form.instagram} onChange={(e) => set("instagram", e.target.value)} /></Field>
            <div className="sm:col-span-2"><Field label="Alamat"><Textarea value={form.address} onChange={(e) => set("address", e.target.value)} /></Field></div>
            <Field label="Kota"><Input value={form.city} onChange={(e) => set("city", e.target.value)} /></Field>
            <Field label="Provinsi"><Input value={form.province} onChange={(e) => set("province", e.target.value)} /></Field>
            <Field label="Kode Pos"><Input value={form.postcode} onChange={(e) => set("postcode", e.target.value)} /></Field>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} data-testid="customer-save-button">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
